import React, { FC, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';

import { dateFormat, formatDate, parseDateString } from '../../utils/dateUtils';
import PropertyDisplay from '../PropertyDisplay/PropertyDisplay';
import FAIcon from '../FAIcon/FAIcon';
import { useClickThruLabel, ClickThruTemplate, ClickThruSection } from '../../hooks/useClickThruLabel';

import './ActorDisplay.css';

interface ActorDisplayProps {
	label: string;
	name?: string | null;
	company?: string | null;
	date?: string | null;
	emailAddress?: string | null;
	showEmailAddressAsLink?: boolean;
	orderId?: string | null;
	click_thru_template?: ClickThruTemplate & {
		Title: string;
		Version: {
			label: string;
			text: string;
		};
	};
}
const ActorDisplay: FC<ActorDisplayProps> = ({
	label,
	name,
	company,
	emailAddress,
	date,
	orderId,
	showEmailAddressAsLink = false,
	click_thru_template,
}) => {
	const [showAcknowledgement, setShowAcknowledgement] = useState(false);

	const { renderLabelWithLink, notFoundMessage, clearNotFoundMessage } =
		useClickThruLabel(click_thru_template);

	const acknowledgementSection = click_thru_template?.Sections.find(
		(s: ClickThruSection) => s.type === 'acknowledgement-checkbox'
	);

	const renderToggle = () => {
		const handleClick = (e: React.MouseEvent) => {
			e.preventDefault();
			setShowAcknowledgement(!showAcknowledgement);
		};

		const toggleText = showAcknowledgement ? 'Hide acknowledgement' : 'Show acknowledgement';

		return (
			<span
				onClick={handleClick}
				className="ActorDisplay__toggle cursor-pointer"
				role="button"
				tabIndex={0}
				onKeyPress={(e) => {
					if (e.key === 'Enter' || e.key === ' ') {
						handleClick(e as any);
					}
				}}
			>
				<FAIcon
					className="mr-1"
					name={showAcknowledgement ? 'chevron-up' : 'chevron-down'}
				/>
				{toggleText}
			</span>
		);
	};

	return (
		<>
			<PropertyDisplay label={label}>
				{name}
				{emailAddress &&
					(showEmailAddressAsLink ? (
						<>
							{' '}
							(
							<a href={`mailto:${emailAddress}?subject=Regarding%20order%20${orderId}`}>
								{emailAddress}
							</a>
							)
						</>
					) : (
						<> ({emailAddress})</>
					))}
				{name && company && ', '}
				{company}
				{date && (
					<>
						<br />
						<small>{formatDate(parseDateString(date), dateFormat.DATETIME_PT)}</small>
					</>
				)}
				{acknowledgementSection && (
					<>
						<br />
						{renderToggle()}
						{showAcknowledgement && (
							<>
								<br />
								<div className="ActorDisplay__acknowledgement-box">
									<span className="ActorDisplay__checkbox-icon">
										<FAIcon name="check" />
									</span>
									<span className="ActorDisplay__acknowledgement-text">
										{renderLabelWithLink(acknowledgementSection.label)}
									</span>
								</div>
								{click_thru_template?.Version && (
									<>
										<br />
										<small style={{ display: 'block', textAlign: 'right' }}>
											{click_thru_template.Version.label}: {click_thru_template.Version.text}
										</small>
									</>
								)}
							</>
						)}
					</>
				)}
			</PropertyDisplay>

			<Modal show={!!notFoundMessage} onHide={clearNotFoundMessage} centered>
				<Modal.Header closeButton>
					<Modal.Title>Notice</Modal.Title>
				</Modal.Header>
				<Modal.Body>{notFoundMessage}</Modal.Body>
				<Modal.Footer>
					<Button variant="primary" onClick={clearNotFoundMessage}>
						OK
					</Button>
				</Modal.Footer>
			</Modal>
		</>
	);
};

export default ActorDisplay;
